import React, { useMemo } from 'react'
import { useEffectiveCatalogQuery, useL10nMessages } from '~/api/l10n'
import { isClient, useIsMounting, isServer } from '~/common/nextJsUtils'
import { MutedSkeleton } from '~/common/skeletons'
import { createIntl, useIntlContext, ValeyardIntlProvider } from './IntlProviders.context'
import { Expression } from '../common/valeyard/runtime'
import { VndlySpinner } from './VndlySpinner'

/**
 * useIntl but without formatMessage, to format a message please use our Msg or useMsg utils
 *
 * @agent-offlimits i18n provider infrastructure; wired up by the app shell, do not nest
 */
export function useIntl() {
  const intl = useIntlContext().intl
  return useMemo(
    () => ({
      locale: intl.locale,
      timeZone: intl.timeZone!,
      timezone: intl.timeZone!, // alias with lower 'z' 'cause we are inconsistent
      formatDateTimeRange: intl.formatDateTimeRange,
      formatDate: intl.formatDate,
      formatTime: intl.formatTime,
      formatDateToParts: intl.formatDateToParts,
      formatTimeToParts: intl.formatTimeToParts,
      formatRelativeTime: intl.formatRelativeTime,
      formatNumber: intl.formatNumber,
      formatNumberToParts: intl.formatNumberToParts,
      formatPlural: intl.formatPlural,
      formatList: intl.formatList,
      formatListToParts: intl.formatListToParts,
      formatDisplayName: intl.formatDisplayName
    }),
    [intl]
  )
}

export function IntlProviderFromApi({
  anonymous,
  children
}: {
  anonymous: boolean
  children: React.ReactNode | undefined
}) {
  const { messages, locale_tag, timezone, allLayeredLoaded } = useL10nMessages({ anonymous })
  const isMounting = useIsMounting()
  // We don't want to show the spinner on Django pages
  const hideSpinner = isServer() || !window.__NEXTJS__ || Boolean(isMounting || (allLayeredLoaded && locale_tag))
  const hideContent = Boolean(isMounting || !allLayeredLoaded || !locale_tag)

  const intl = useCreateIntl(locale_tag, timezone)
  useSetHtmlLangAttr(locale_tag)

  // If there is already an IntlProvider higher up in the React tree, then we don't need another one
  if (useAlreadyWithinIntlProvider()) return children

  // console.log('IntlProviderFromApi', {
  //   showContent: (!hideContent && hideSpinner && '✅') || '❌',
  //   allLayeredLoaded: (allLayeredLoaded && '✅') || '❌',
  //   meLoaded: (locale_tag && timezone && '✅') || '❌'
  // })

  /*
  We want to show and hide the spinner and contents using native `hidden` attribute,
  so that all the rendered dom content is present in the statically rendered page.
  As soon as the l10n messages are loaded it's a simple attribute change to show the
  contents, instead of needing to have React render it all from scratch at runtime.
  */
  // @ts-ignore
  return (
    <>
      <VndlySpinner hidden={hideSpinner} />
      <div hidden={hideContent}>
        <ValeyardIntlProvider intl={intl} messages={messages} allLayersLoaded={allLayeredLoaded}>
          {children}
        </ValeyardIntlProvider>
      </div>
    </>
  )
}

export interface PreBundledCatalog {
  catalog: Record<string, Expression>
  locale: string
  locale_tag: string
  timezone: string
}

export function useAlreadyWithinIntlProvider() {
  let alreadyWithinIntlProvider = false
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    alreadyWithinIntlProvider = Boolean(useIntlContext())
    // eslint-disable-next-line no-empty
  } catch {}
  return alreadyWithinIntlProvider
}

function useCreateIntl(locale_tag: string | undefined, timezone: string | undefined) {
  return useMemo(() => createIntl({ locale_tag, timezone }), [locale_tag, timezone])
}

function useSetHtmlLangAttr(locale: string | undefined) {
  React.useEffect(() => {
    if (locale && isClient() && document?.documentElement?.lang !== locale) {
      document.documentElement.lang = locale
    }
  }, [locale])
}

type LocaleOverrideProviderProps = {
  enabled?: boolean
  locale?: string
  children: React.ReactNode
  tag?: string
}

/**
 * Provider that can override the site-wide locale context
 * Falls back to parent context if no locale is passed, enabled == false, or if the API request fails
 */
export function LocaleOverrideProvider({
  enabled = true,
  locale,
  children,
  tag = 'vndly'
}: LocaleOverrideProviderProps) {
  const useLocaleOverride = enabled && Boolean(locale)
  const { data, isSuccess, isLoading, isError } = useEffectiveCatalogQuery(
    { locale, tag },
    { enabled: useLocaleOverride }
  )

  // Fall back to parent context if override is disabled or if the API request failed
  if (!useLocaleOverride || isError) {
    return <>{children}</>
  }

  const intl = createIntl({ locale_tag: data?.locale_tag, timezone: data?.timezone })

  return (
    <ValeyardIntlProvider intl={intl} messages={data?.catalog ?? {}} allLayersLoaded={isSuccess && !isLoading}>
      <MutedSkeleton isLoaded={isSuccess}>{children}</MutedSkeleton>
    </ValeyardIntlProvider>
  )
}
